// 檢視 / 設備群組編輯（SPEC #org-tree-ui 第 4 點、#org-tree-views、o16 / o17）：
//   - 來源 = 編輯時能參照的東西（選單 11）：組織檢視 / 群組 = 本組織的結構樹、委派給本組織的節點與其中的家戶與設備名稱（GET …/delegations/{d}/nodes?include=devices），
//     只能選、不顯示影像與事件，編輯者不需要資料權；個人檢視 / 群組 = 我看得到的（/v1/me/tree）
//   - 失效項目只顯示「N 個項目已無法參照」/「來自已結束的委派：<委派方>（N 個項目）」+〔移除〕，不顯示原本的名稱
//   - 檢視：資料夾（深度 ≤ 8）+ 參照；群組：平面集合（節點、家戶、設備）
//   - 整份替換（CAS）；上限超過 429 TREE_LIMIT
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { isOrgAdmin, type MyOrg } from '../../lib/planeApi';
import { meTreeApi, treeApi, type DeviceGroup, type MeTreeItem, type View, type ViewItem } from '../../lib/orgTreeApi';
import { levelText } from '../../lib/levels';
import { DelegatedNodePicker, toggle } from './DelegatedNodePicker';
import { StructureTree } from './StructureTree';
import { refOf } from './AddToDialog';
import { useViewsAndGroups } from './hooks';
import { otErr } from './otErr';

let seq = 0;
const newId = () => `new-${Date.now().toString(36)}-${++seq}`;

export function ViewEditor({ kind, initial, close }: { kind: 'view' | 'group'; initial: View | DeviceGroup | null; close(): void }) {
  const { t } = useTranslation();
  const { planes, orgs } = useScope();
  const qc = useQueryClient();
  const adminOrgs = orgs.filter((o) => o.kind === 'team' && isOrgAdmin(o));
  const [owner, setOwner] = useState<string>(initial ? (initial.owner_kind === 'user' ? 'user' : initial.owner_id) : 'user');
  const org = adminOrgs.find((o) => o.org_id === owner) ?? orgs.find((o) => o.org_id === owner) ?? null;
  const [name, setName] = useState(initial?.name ?? '');
  const [items, setItems] = useState<ViewItem[]>(() => (initial?.items ?? []).map((i) => ({ ...i, item_id: i.item_id ?? newId() })));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const plane = org ? { api: org.api } : planes[0];

  const ok = items.filter((i) => !i.unavailable);
  const ended = items.filter((i) => i.unavailable === 'delegation_ended');
  const gone = items.filter((i) => i.unavailable === 'gone');
  const endedBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of ended) m.set(i.from_org_name ?? '', (m.get(i.from_org_name ?? '') ?? 0) + 1);
    return [...m.entries()];
  }, [ended]);
  const folders = ok.filter((i) => i.kind === 'folder');

  function add(it: ViewItem) {
    if (items.some((x) => x.kind === it.kind && x.ref === it.ref && !x.unavailable)) return;
    setItems([...items, { ...it, item_id: newId(), parent_item_id: null }]);
  }
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const o = owner === 'user' ? ({ kind: 'user' } as const) : ({ kind: 'org', org_id: owner } as const);
      const body = items.map(({ item_id, parent_item_id, kind: k, ref, name: n }) => ({ item_id, parent_item_id: parent_item_id ?? null, kind: k, ...(ref ? { ref } : {}), ...(k === 'folder' ? { name: n } : {}) }));
      if (kind === 'view') await meTreeApi.saveView(plane, o, { view_id: (initial as View | null)?.view_id, name: name.trim(), items: body, expected_version: initial?.version });
      else await meTreeApi.saveGroup(plane, o, { group_id: (initial as DeviceGroup | null)?.group_id, name: name.trim(), items: body, expected_version: initial?.version });
      void qc.invalidateQueries({ queryKey: ['me-tree'] });
      void qc.invalidateQueries({ queryKey: ['me-scope'] });
      close();
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }

  const title = initial
    ? t(kind === 'view' ? 'ot.view_edit_title' : 'ot.group_edit_dev_title', { name: initial.name })
    : t(kind === 'view' ? 'ot.view_new' : 'ot.group_dev_new');
  return (
    <Dialog open title={title} onClose={close} wide>
      <div className="form" data-testid={kind === 'view' ? 'view-editor' : 'group-editor'}>
        <p className="muted small">{t('ot.views_hint')}</p>
        <div className="row">
          <label className="field" style={{ flex: 1 }}>
            <span>{t('ot.name')}</span>
            <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('ot.owner')}</span>
            <select className="input" value={owner} disabled={!!initial} onChange={(e) => (setOwner(e.target.value), setItems([]))}>
              <option value="user">{t('ot.personal')}</option>
              {adminOrgs.map((o) => (
                <option key={o.org_id} value={o.org_id}>
                  {t('ot.org_view_of', { org: o.display_name })}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="ot-editor">
          <section className="ot-source card">
            <h3 className="ot-h3">{owner === 'user' ? t('ot.source_personal') : t('ot.source_org')}</h3>
            {owner === 'user' ? <PersonalSource add={add} kind={kind} /> : org && <OrgSource org={org} add={add} kind={kind} />}
          </section>
          <section className="ot-content card" data-testid="editor-content">
            <div className="toolbar">
              <h3 className="ot-h3" style={{ margin: 0 }}>
                {kind === 'view' ? t('ot.view_content') : t('ot.group_content')}
              </h3>
              <span className="spacer" />
              {kind === 'view' && (
                <button className="btn btn-sm" onClick={() => setItems([...items, { item_id: newId(), parent_item_id: null, kind: 'folder', name: t('ot.new_folder') }])}>
                  {t('ot.add_folder')}
                </button>
              )}
            </div>
            {ok.length === 0 && <p className="muted small">{t('ot.editor_empty')}</p>}
            <ul className="plain-list">
              {ok.map((i) => (
                <li key={i.item_id} className="row ot-edit-item" data-edit-item={i.name ?? i.ref}>
                  {i.kind === 'folder' ? (
                    <input className="input" style={{ maxWidth: 200 }} value={i.name ?? ''} onChange={(e) => setItems(items.map((x) => (x.item_id === i.item_id ? { ...x, name: e.target.value } : x)))} aria-label={t('ot.folder')} />
                  ) : (
                    <span>
                      {i.kind === 'group' ? t('ot.group_prefix', { name: i.name ?? '' }) : i.name}
                      {i.from_org_name && <span className="ot-dlg-mark small"> ⤳ {i.from_org_name}</span>}
                      <span className="muted small"> {t('ot.ref_' + i.kind)}</span>
                    </span>
                  )}
                  <span className="spacer" />
                  {kind === 'view' && folders.length > 0 && i.kind !== 'folder' && (
                    <select className="input" style={{ width: 'auto' }} value={i.parent_item_id ?? ''} onChange={(e) => setItems(items.map((x) => (x.item_id === i.item_id ? { ...x, parent_item_id: e.target.value || null } : x)))} aria-label={t('ot.folder')}>
                      <option value="">{t('ot.top_level')}</option>
                      {folders.map((f) => (
                        <option key={f.item_id} value={f.item_id}>
                          📁 {f.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button className="icon-btn" aria-label={t('common.action_remove')} onClick={() => setItems(items.filter((x) => x.item_id !== i.item_id && x.parent_item_id !== i.item_id))}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
            {endedBy.map(([from, n]) => (
              <p key={from} className="ot-warn small row" data-testid="ended-items">
                ⚠ {t('ot.items_from_ended', { from, count: n })}
                <button className="btn btn-sm" onClick={() => setItems(items.filter((x) => !(x.unavailable === 'delegation_ended' && (x.from_org_name ?? '') === from)))}>
                  {t('common.action_remove')}
                </button>
              </p>
            ))}
            {gone.length > 0 && (
              <p className="ot-warn small row" data-testid="gone-items">
                {t('ot.items_gone', { count: gone.length })}
                <button className="btn btn-sm" onClick={() => setItems(items.filter((x) => x.unavailable !== 'gone'))}>
                  {t('common.action_remove')}
                </button>
              </p>
            )}
          </section>
        </div>
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void save()} data-testid="editor-save">
            {t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** 組織檢視 / 群組的來源：本組織的結構、委派給本組織的節點（只有名稱）、本組織的設備群組。 */
function OrgSource({ org, add, kind }: { org: MyOrg; add(i: ViewItem): void; kind: 'view' | 'group' }) {
  const { t } = useTranslation();
  const dlgs = useQuery({ queryKey: ['ot', org.org_id, 'dlg', 'in'], queryFn: () => treeApi.delegations(org, 'in'), retry: false });
  const { groups } = useViewsAndGroups([], [org]);
  return (
    <div className="ot-source-body">
      <h4 className="small muted">{t('ot.source_structure')}</h4>
      <StructureTree org={org} showLock={false} showUnassigned={false} onAdd={(it) => add({ kind: it.kind === 'home' ? 'home' : 'node', ref: it.kind === 'home' ? it.home_id : it.node_id, name: it.name })} />
      {(dlgs.data ?? [])
        .filter((d) => d.status === 'active')
        .map((d) => (
          <div key={d.delegation_id} data-testid="source-dlg">
            <h4 className="small muted">{t('ot.dlg_label', { from: d.from_org_name })}</h4>
            <DelegatedNodePicker
              org={org}
              delegation={d}
              devices
              onAdd={(it) =>
                add(
                  it.device_ref
                    ? { kind: 'device', ref: `${it.home_id}/${it.device_ref}`, name: it.name, delegation_id: d.delegation_id, from_org_name: d.from_org_name }
                    : { kind: it.kind === 'home' ? 'home' : 'node', ref: it.kind === 'home' ? it.home_id : it.node_id, name: it.name, delegation_id: d.delegation_id, from_org_name: d.from_org_name },
                )
              }
            />
          </div>
        ))}
      {kind === 'view' && (groups.data ?? []).length > 0 && (
        <>
          <h4 className="small muted">{t('ot.tab_groups')}</h4>
          <ul className="plain-list">
            {groups.data!.map((g) => (
              <li key={g.group_id} className="row">
                ▦ {g.name}
                <span className="spacer" />
                <button className="btn btn-sm ot-add" onClick={() => add({ kind: 'group', ref: g.group_id, name: g.name })} aria-label={t('ot.add_ref', { name: g.name })}>
                  ＋
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** 個人檢視 / 群組的來源：我看得到的（/v1/me/tree；家戶展開列設備）。 */
function PersonalSource({ add, kind }: { add(i: ViewItem): void; kind: 'view' | 'group' }) {
  const { t } = useTranslation();
  const { planes } = useScope();
  const top = useQuery({ queryKey: ['me-tree', 'level', planes[0]?.api, ''], queryFn: () => meTreeApi.level(planes[0]), enabled: planes.length > 0 });
  const { groups } = useViewsAndGroups(planes, []);
  return (
    <div className="ot-source-body">
      <ul className="ot-tree" role="tree">
        {(top.data ?? []).map((it) => (
          <MeSourceRow key={it.key} it={{ ...it, _api: planes[0].api }} depth={0} add={add} />
        ))}
      </ul>
      {kind === 'view' && (groups.data ?? []).length > 0 && (
        <ul className="plain-list">
          {groups.data!.map((g) => (
            <li key={g.group_id} className="row">
              ▦ {g.name}
              <span className="spacer" />
              <button className="btn btn-sm ot-add" onClick={() => add({ kind: 'group', ref: g.group_id, name: g.name })} aria-label={t('ot.add_ref', { name: g.name })}>
                ＋
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MeSourceRow({ it, depth, add }: { it: MeTreeItem & { _api: string }; depth: number; add(i: ViewItem): void }) {
  const { t } = useTranslation();
  const { devices } = useScope();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const isOpen = open.has(it.key);
  const kids = useQuery({ queryKey: ['me-tree', 'level', it._api, it.key], queryFn: () => meTreeApi.level({ api: it._api }, it.key), enabled: isOpen && it.kind !== 'home' });
  const homeDevs = it.kind === 'home' ? devices.filter((d) => d.home_id === (it.home_id ?? it.id)) : [];
  const addable = it.kind === 'node' || it.kind === 'home';
  const blocked = it.delegation?.notice === 'delegator_mfa_required' || it.status === 'ended';
  return (
    <li role="treeitem">
      <div className="ot-row" style={{ paddingLeft: depth * 14 }}>
        {!blocked && (it.has_children !== false || it.kind === 'home') ? (
          <button className="ot-twisty" aria-label={isOpen ? t('ot.collapse') : t('ot.expand')} onClick={() => setOpen((s) => toggle(s, it.key))}>
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="ot-twisty" />
        )}
        <span>{it.kind === 'delegation' ? t('ot.dlg_label', { from: it.delegation?.from_org_name ?? it.name }) : it.name}</span>
        {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
        <span className="spacer" />
        {addable && (
          <button className="btn btn-sm ot-add" onClick={() => add(refOf(it))} aria-label={t('ot.add_ref', { name: it.name })}>
            ＋
          </button>
        )}
      </div>
      {isOpen && (
        <ul role="group">
          {it.kind === 'home'
            ? homeDevs.map((d) => (
                <li key={d.device_ref} className="ot-row" style={{ paddingLeft: (depth + 1) * 14 }}>
                  <span className="ot-twisty" />
                  {d.display_name}
                  <span className="spacer" />
                  <button
                    className="btn btn-sm ot-add"
                    onClick={() => add({ kind: 'device', ref: `${d.home_id}/${d.device_ref}`, name: d.display_name })}
                    aria-label={t('ot.add_ref', { name: d.display_name })}
                  >
                    ＋
                  </button>
                </li>
              ))
            : (kids.data ?? []).map((x) => <MeSourceRow key={x.key} it={{ ...x, _api: it._api, delegation: x.delegation ?? it.delegation ?? null }} depth={depth + 1} add={add} />)}
        </ul>
      )}
    </li>
  );
}
